import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, of, forkJoin } from 'rxjs';
import { Audience } from '../models/audience.model';
import { map, switchMap, catchError } from 'rxjs/operators';

export interface VeevaAudience {
  id: number;
  name: string;
  label: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface VeevaSpecialist {
  id: number;
  name: string;
  label: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateUserAudienceRequest {
  title: string;
  description: string;
  color_code: string;
  audience_type: 'audience' | 'specialist';
  veeva_audience_id?: number | null;
  veeva_specialist_id?: number | null;
}

export interface UserAudienceResponse {
  id: number;
  title: string;
  description: string;
  color_code: string;
  audience_type: string;
  veeva_audience_id?: number | null;
  veeva_specialist_id?: number | null;
  created_at: string;
  updated_at: string;
}

export interface InUseAudience {
  id: number;
  name: string;
}

export type InUseAudiencesResponse = InUseAudience[];

export interface UnifiedAudience {
  id: number;
  name: string;
  veeva_audience_id: number | null;
  veeva_specialist_id: number | null;
}

export interface VeevaTaggedAudience {
  veeva_audience_id: number | null;
  veeva_specialist_id: number | null;
  name: string;
}

@Injectable({
  providedIn: 'root'
})
export class AudienceService {
  private apiUrl = '/api/audiences';
  private refreshSubject = new Subject<void>();
  
  refresh$ = this.refreshSubject.asObservable();

  constructor(private http: HttpClient) {}

  // Placeholder method - returns empty array until backend is ready
  getAllAudiences(): Observable<Audience[]> {
    // TODO: Replace with actual API call when backend is ready
    // return this.http.get<Audience[]>(this.apiUrl);
    console.log('getAllAudiences placeholder - returning empty array');
    return of([]);
  }

  // Placeholder method - returns mock audience until backend is ready
  getAudienceById(id: number): Observable<Audience> {
    // TODO: Replace with actual API call when backend is ready
    // return this.http.get<Audience>(`${this.apiUrl}/${id}`);
    console.log('getAudienceById placeholder for id:', id);
    return of({
      id,
      name: 'Mock Audience',
      description: 'Mock Description',
      color_code: '#6366F1',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    });
  }

  // Placeholder method - returns mock audience until backend is ready
  createAudience(audience: Omit<Audience, 'id' | 'created_at' | 'updated_at'>): Observable<Audience> {
    // TODO: Replace with actual API call when backend is ready
    // return this.http.post<Audience>(this.apiUrl, audience);
    console.log('createAudience placeholder:', audience);
    return of({
      id: Date.now(),
      ...audience,
      color_code: (audience as any).color_code || '#6366F1',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    });
  }

  // Placeholder method - returns mock audience until backend is ready
  updateAudience(id: number, audience: Partial<Audience>): Observable<Audience> {
    // TODO: Replace with actual API call when backend is ready
    // return this.http.patch<Audience>(`${this.apiUrl}/${id}`, audience);
    console.log('updateAudience placeholder:', id, audience);
    return of({
      id,
      name: audience.name || 'Updated Audience',
      description: audience.description || 'Updated Description',
      color_code: (audience as any).color_code || '#6366F1',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    });
  }

  // Placeholder method - returns success until backend is ready
  deleteAudience(id: number): Observable<void> {
    // TODO: Replace with actual API call when backend is ready
    // return this.http.delete<void>(`${this.apiUrl}/${id}`);
    console.log('deleteAudience placeholder for id:', id);
    return of(undefined);
  }

  triggerRefresh(): void {
    this.refreshSubject.next();
  }

  // New: fetch Veeva audiences from backend
  getVeevaAudiences(active: boolean = true): Observable<VeevaAudience[]> {
    const url = `/api/veeva/audiences`;
    const params: any = { active: String(!!active) };
    return this.http.get<VeevaAudience[]>(url, { params }).pipe(
      map(res => res || [])
    );
  }

  // New: fetch Veeva specialists from backend with optional filters
  getVeevaSpecialists(filters?: { active?: boolean; name?: string; label?: string }): Observable<VeevaSpecialist[]> {
    const url = `/api/veeva/specialists`;
    const params: any = {};
    if (filters) {
      if (filters.active !== undefined) params.active = String(filters.active);
      if (filters.name) params.name = filters.name;
      if (filters.label) params.label = filters.label;
    } else {
      params.active = 'true';
    }
    return this.http.get<VeevaSpecialist[]>(url, { params }).pipe(
      map(res => res || [])
    );
  }

  // New: create a user-defined audience
  createUserAudience(request: CreateUserAudienceRequest): Observable<UserAudienceResponse> {
    return this.http.post<UserAudienceResponse>('/api/user-audiences', request);
  }

  // New: get all user-defined audiences
  getUserAudiences(): Observable<UserAudienceResponse[]> {
    return this.http.get<UserAudienceResponse[]>('/api/user-audiences').pipe(
      catchError(error => {
        console.error('getUserAudiences failed:', error.message);
        return of([]);
      })
    );
  }

  // New: get all audiences and specialists currently in use
  getInUseAudiences(): Observable<InUseAudiencesResponse> {
    return this.http.get<InUseAudiencesResponse>('/api/audiences/in-use').pipe(
      catchError(error => {
        console.error('getInUseAudiences failed:', error.message);
        return of([] as InUseAudiencesResponse);
      })
    );
  }

  // New: get all unified audiences (Veeva audiences, Veeva specialists, and user audiences)
  getUnifiedAudiences(): Observable<UnifiedAudience[]> {
    return this.http.get<UnifiedAudience[]>('/api/unified-audiences');
  }

  // New: update a user-defined audience
  updateUserAudience(id: number, request: CreateUserAudienceRequest): Observable<UserAudienceResponse> {
    return this.http.patch<UserAudienceResponse>(`/api/user-audiences/${id}`, request);
  }

  // New: delete a user-defined audience
  deleteUserAudience(id: number): Observable<void> {
    return this.http.delete<void>(`/api/user-audiences/${id}`);
  }

  // Get Veeva audiences tagged to a specific entity
  getTaggedAudiences(entityType: string, entityId: string): Observable<VeevaTaggedAudience[]> {
    return this.http.get<VeevaTaggedAudience[]>(`/api/audiences/tagged/${entityType}/${entityId}`);
  }

  // New: Tag an audience to a specific entity
  tagAudienceToEntity(audienceId: number, entityType: string, entityId: string): Observable<any> {
    const payload = {
      entityType: entityType,
      entityId: entityId,
      audienceId: audienceId
    };
    return this.http.post('/api/audiences/tag', payload);
  }

  // New: Untag an audience from a specific entity
  untagAudienceFromEntity(audienceId: number, entityType: string, entityId: string): Observable<any> {
    const payload = {
      entityType: entityType,
      entityId: entityId,
      audienceId: audienceId
    };
    return this.http.post('/api/audiences/untag', payload);
  }

  // Bulk update audience assignments for an entity
  // NOTE: This method is deprecated - we now send audiences directly via the /edit endpoint
  // Keeping for reference but not used in current implementation
  /*
  updateMultipleAudienceAssignments(entityType: string, entityId: string, audienceIds: number[]): Observable<{success: boolean}> {
    // First get currently tagged audiences
    return this.getTaggedAudiences(entityType, entityId).pipe(
      switchMap(currentAudiences => {
        const currentIds = currentAudiences.map(a => a.veeva_audience_id);
        const toAdd = audienceIds.filter(id => !currentIds.includes(id));
        const toRemove = currentIds.filter(id => !audienceIds.includes(id));
        
        const operations: Observable<any>[] = [];
        
        // Add new audiences
        toAdd.forEach(audienceId => {
          operations.push(this.tagAudienceToEntity(audienceId, entityType, entityId));
        });
        
        // Remove unselected audiences  
        toRemove.forEach(audienceId => {
          operations.push(this.untagAudienceFromEntity(audienceId, entityType, entityId));
        });
        
        if (operations.length === 0) {
          return of({success: true});
        }
        
        return forkJoin(operations).pipe(
          map(() => ({success: true})),
          catchError(error => {
            console.error('Error in bulk audience assignment:', error);
            return of({success: false});
          })
        );
      })
    );
  }
  */
}
